import React from 'react';
import confetti from 'canvas-confetti';
import { AthleteProfile, Badge, ProHighlight } from '../types';

export interface AutomatedBadgeThreshold {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'basketball' | 'soccer' | 'general' | 'elite_series';
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'pro_elite' | 'legendary';
  targetValue: number;
  xpReward: number;
  isProVerified?: boolean;
  check: (user: AthleteProfile) => { isUnlocked: boolean; currentValue: number };
}

/**
 * Triggers a unique Elite Series celebratory confetti blast.
 */
export function fireEliteSeriesConfetti() {
  if (typeof window !== 'undefined') {
    confetti({
      particleCount: 150,
      spread: 100,
      origin: { y: 0.55 },
      colors: ['#F59E0B', '#FCD34D', '#10B981', '#6366F1', '#EC4899', '#A855F7'],
    });
  }
}

/**
 * CSS filter utility that applies distinct glow or shine effects to the Profile badge display
 * ONLY when a user achieves the highest 'Legendary' tier, visually distinguishing top-tier athletes from standard users.
 */
export function getLegendaryBadgeFilter(tier?: string): React.CSSProperties | undefined {
  if (tier === 'legendary') {
    return {
      filter: 'drop-shadow(0 0 12px rgba(251, 191, 36, 0.9)) drop-shadow(0 0 24px rgba(245, 158, 11, 0.75)) brightness(1.25) contrast(1.15)',
    };
  }
  return undefined;
}

/**
 * Check if athlete has unlocked all three primary tiers (or high tiers) for their sport.
 */
export function hasUnlockedAllTiersForSport(user: AthleteProfile, targetSport?: string): boolean {
  if (!user) return false;
  const sport = targetSport || user.primarySport || 'basketball';
  const userBadges = user.badges || [];
  
  // Check if user has unlocked badges across bronze/silver/gold or pro_elite/legendary tiers
  const unlockedTiers = new Set<string>();
  userBadges.forEach((b) => {
    if (b.unlocked && (b.category === sport || b.category === 'elite_series' || b.category === 'general')) {
      if (b.tier) unlockedTiers.add(b.tier);
    }
  });

  // Returns true if user has unlocked at least 3 distinct tiers (e.g., bronze/silver/gold or pro_elite/legendary)
  return unlockedTiers.size >= 3 || (unlockedTiers.has('pro_elite') && unlockedTiers.has('legendary')) || unlockedTiers.has('gold');
}

export const CAREER_BADGE_THRESHOLDS: AutomatedBadgeThreshold[] = [
  {
    id: 'badge_100_pts',
    name: 'Century Scorer 💯',
    description: 'Cross 100 total career points across all playground matches.',
    icon: 'Trophy',
    category: 'basketball',
    tier: 'gold',
    targetValue: 100,
    xpReward: 500,
    check: (user) => {
      const pts = (user.stats?.basketball?.pts || 0) + (user.stats?.soccer?.goals || 0) * 3;
      return { isUnlocked: pts >= 100, currentValue: pts };
    },
  },
  {
    id: 'badge_50_reb',
    name: 'Glass Cleaner 🛡️',
    description: 'Collect 50 total career rebounds in basketball or volleyball.',
    icon: 'ShieldCheck',
    category: 'basketball',
    tier: 'silver',
    targetValue: 50,
    xpReward: 400,
    check: (user) => {
      const reb = (user.stats?.basketball?.reb || 0) + (user.stats?.volleyball?.kills || 0);
      return { isUnlocked: reb >= 50, currentValue: reb };
    },
  },
  {
    id: 'badge_25_ast',
    name: 'Master Playmaker 🎯',
    description: 'Record 25 total career assists across pickup runs.',
    icon: 'Zap',
    category: 'basketball',
    tier: 'silver',
    targetValue: 25,
    xpReward: 350,
    check: (user) => {
      const ast = (user.stats?.basketball?.ast || 0) + (user.stats?.soccer?.assists || 0);
      return { isUnlocked: ast >= 25, currentValue: ast };
    },
  },
  {
    id: 'badge_10_games',
    name: 'Court Regular 👟',
    description: 'Complete 10 registered pickup runs in the league.',
    icon: 'Activity',
    category: 'general',
    tier: 'bronze',
    targetValue: 10,
    xpReward: 300,
    check: (user) => {
      const games = (user.stats?.basketball?.gamesPlayed || 0) + (user.winCount + user.lossCount);
      return { isUnlocked: games >= 10, currentValue: games };
    },
  },
  {
    id: 'badge_5_wins',
    name: 'Clutch Winner 🏆',
    description: 'Secure 5 match victories on the playground courts.',
    icon: 'Crown',
    category: 'general',
    tier: 'silver',
    targetValue: 5,
    xpReward: 300,
    check: (user) => {
      return { isUnlocked: user.winCount >= 5, currentValue: user.winCount };
    },
  },
  // VOLLEYBALL SPECIFIC BADGE THRESHOLDS
  {
    id: 'badge_vb_backcourt_wall',
    name: 'Backcourt Wall 🛡️',
    description: 'Logged 50+ total ground digs on volleyball courts.',
    icon: 'ShieldCheck',
    category: 'volleyball' as any,
    tier: 'gold',
    targetValue: 50,
    xpReward: 450,
    check: (user) => {
      const digs = user.stats?.volleyball?.digs || 0;
      return { isUnlocked: digs >= 50, currentValue: digs };
    },
  },
  {
    id: 'badge_vb_ace_striker',
    name: 'Ace Striker ⚡',
    description: 'Hit 50+ total unreturnable service aces in volleyball matches.',
    icon: 'Zap',
    category: 'volleyball' as any,
    tier: 'platinum',
    targetValue: 50,
    xpReward: 500,
    check: (user) => {
      const aces = user.stats?.volleyball?.aces || 0;
      return { isUnlocked: aces >= 50, currentValue: aces };
    },
  },
  {
    id: 'badge_vb_block_party',
    name: 'Block Party 🧱',
    description: 'Rejected 20+ opponent spike attempts with net blocks in volleyball.',
    icon: 'Trophy',
    category: 'volleyball' as any,
    tier: 'gold',
    targetValue: 20,
    xpReward: 400,
    check: (user) => {
      const blocks = user.stats?.volleyball?.blocks || 0;
      return { isUnlocked: blocks >= 20, currentValue: blocks };
    },
  },
  {
    id: 'badge_vb_spike_king',
    name: 'Spike King 🏐',
    description: 'Hammered 50+ total spike kills down the net in volleyball.',
    icon: 'Flame',
    category: 'volleyball' as any,
    tier: 'gold',
    targetValue: 50,
    xpReward: 450,
    check: (user) => {
      const kills = user.stats?.volleyball?.kills || 0;
      return { isUnlocked: kills >= 50, currentValue: kills };
    },
  },
  // ELITE SERIES BADGES (Exclusively for Verified PRO Athletes)
  {
    id: 'badge_pro_vanguard_scorer',
    name: 'Pro Vanguard Scorer 👑',
    description: 'PRO Verified Milestone: Reach 120+ verified career points in official league games.',
    icon: 'Sparkles',
    category: 'elite_series',
    tier: 'pro_elite',
    targetValue: 120,
    xpReward: 1000,
    isProVerified: true,
    check: (user) => {
      const isPro = !!(user.isPro || user.isVerifiedPro || user.subscriptionTier === 'pro');
      const pts = (user.stats?.basketball?.pts || 0) + (user.stats?.soccer?.goals || 0) * 3;
      return { isUnlocked: isPro && pts >= 120, currentValue: pts };
    },
  },
  {
    id: 'badge_pro_playmaker',
    name: 'Scout Certified Playmaker 👁️',
    description: 'PRO Verified Milestone: Reach 30+ verified assists logged with video highlights.',
    icon: 'Eye',
    category: 'elite_series',
    tier: 'pro_elite',
    targetValue: 30,
    xpReward: 850,
    isProVerified: true,
    check: (user) => {
      const isPro = !!(user.isPro || user.isVerifiedPro || user.subscriptionTier === 'pro');
      const ast = (user.stats?.basketball?.ast || 0) + (user.stats?.soccer?.assists || 0);
      return { isUnlocked: isPro && ast >= 30, currentValue: ast };
    },
  },
  {
    id: 'badge_pro_legend_wins',
    name: 'Verified Court Legend 💎',
    description: 'PRO Verified Milestone: Secure 8+ verified wins in PRO league matchups.',
    icon: 'Award',
    category: 'elite_series',
    tier: 'pro_elite',
    targetValue: 8,
    xpReward: 1200,
    isProVerified: true,
    check: (user) => {
      const isPro = !!(user.isPro || user.isVerifiedPro || user.subscriptionTier === 'pro');
      return { isUnlocked: isPro && user.winCount >= 8, currentValue: user.winCount };
    },
  },
  {
    id: 'badge_pro_euro_prospect',
    name: 'EuroLeague Prospect 🌍',
    description: 'PRO Verified Milestone: Reach 150+ PTS and 40+ REB with certified moderator audit.',
    icon: 'Flame',
    category: 'elite_series',
    tier: 'pro_elite',
    targetValue: 150,
    xpReward: 1500,
    isProVerified: true,
    check: (user) => {
      const isPro = !!(user.isPro || user.isVerifiedPro || user.subscriptionTier === 'pro');
      const pts = user.stats?.basketball?.pts || 0;
      return { isUnlocked: isPro && pts >= 150, currentValue: pts };
    },
  },
  // SPORT-SPECIFIC LEGENDARY TIERS (1000+ CAREER POINTS)
  {
    id: 'badge_pro_top_1pct_scorer',
    name: 'Top 1% League Scorer 👑',
    description: 'Elite Series Milestone: Cross 1,000+ career points across verified league competition.',
    icon: 'Crown',
    category: 'elite_series',
    tier: 'legendary',
    targetValue: 1000,
    xpReward: 2500,
    isProVerified: true,
    check: (user) => {
      const isPro = !!(user.isPro || user.isVerifiedPro || user.subscriptionTier === 'pro');
      const pts = (user.stats?.basketball?.pts || 0) + (user.stats?.soccer?.goals || 0) * 3 + (user.stats?.baseball?.hits || 0) * 2;
      return { isUnlocked: isPro && pts >= 1000, currentValue: pts };
    },
  },
  {
    id: 'badge_pro_legendary_hooper',
    name: 'Legendary Hooper 🏀',
    description: 'Elite Series Legendary Tier: 1,000+ Basketball Career Points with verified scout audit.',
    icon: 'Sparkles',
    category: 'basketball',
    tier: 'legendary',
    targetValue: 1000,
    xpReward: 2500,
    isProVerified: true,
    check: (user) => {
      const isPro = !!(user.isPro || user.isVerifiedPro || user.subscriptionTier === 'pro');
      const pts = user.stats?.basketball?.pts || 0;
      return { isUnlocked: isPro && pts >= 1000, currentValue: pts };
    },
  },
  {
    id: 'badge_pro_legendary_striker',
    name: 'Legendary Striker ⚽',
    description: 'Elite Series Legendary Tier: 1,000+ Soccer Career Points / Goal Value with verified audit.',
    icon: 'Trophy',
    category: 'soccer',
    tier: 'legendary',
    targetValue: 1000,
    xpReward: 2500,
    isProVerified: true,
    check: (user) => {
      const isPro = !!(user.isPro || user.isVerifiedPro || user.subscriptionTier === 'pro');
      const pts = (user.stats?.soccer?.goals || 0) * 10 + (user.stats?.soccer?.assists || 0) * 5;
      return { isUnlocked: isPro && pts >= 1000, currentValue: pts };
    },
  },
];

export interface LegendaryNotificationInfo {
  isOpen: boolean;
  badgeId: string;
  badgeName: string;
  title: string;
  description: string;
  currentPoints: number;
  targetPoints: number;
  xpReward: number;
  unlockedAt: string;
}

/**
 * Checks user profile against career badge thresholds.
 * Returns newly unlocked badges, an updated user profile, and optional Legendary Modal notification info.
 */
export function checkAndAwardBadges(user: AthleteProfile): {
  updatedUser: AthleteProfile;
  newlyUnlockedBadges: Badge[];
  legendaryModalNotification?: LegendaryNotificationInfo;
} {
  if (!user) return { updatedUser: user, newlyUnlockedBadges: [] };

  const existingBadgeIds = new Set((user.badges || []).map((b) => b.id));
  const newlyUnlockedBadges: Badge[] = [];
  const currentBadges = [...(user.badges || [])];
  const currentProHighlights: ProHighlight[] = [...(user.proHighlights || [])];

  let bonusXp = 0;
  let triggeredEliteConfetti = false;
  let legendaryModalNotification: LegendaryNotificationInfo | undefined = undefined;

  CAREER_BADGE_THRESHOLDS.forEach((threshold) => {
    const { isUnlocked, currentValue } = threshold.check(user);

    if (isUnlocked && !existingBadgeIds.has(threshold.id)) {
      const newBadge: Badge = {
        id: threshold.id,
        name: threshold.name,
        description: threshold.description,
        icon: threshold.icon,
        category: threshold.category,
        tier: threshold.tier,
        unlocked: true,
        progress: 100,
        unlockedAt: new Date().toISOString().split('T')[0],
        xpReward: threshold.xpReward,
        currentValue,
        targetValue: threshold.targetValue,
        isProVerified: threshold.isProVerified,
      };

      currentBadges.push(newBadge);
      newlyUnlockedBadges.push(newBadge);
      bonusXp += threshold.xpReward;

      // If Elite Series or Legendary, trigger confetti & record PRO Highlight
      if (threshold.category === 'elite_series' || threshold.tier === 'legendary' || threshold.isProVerified) {
        triggeredEliteConfetti = true;
        currentProHighlights.unshift({
          id: `pro_hl_${Date.now()}_${threshold.id}`,
          badgeId: threshold.id,
          title: threshold.name,
          description: threshold.description,
          unlockedAt: new Date().toISOString().split('T')[0],
          tier: threshold.tier === 'legendary' ? 'legendary' : 'pro_elite',
          icon: threshold.icon,
          statsValue: `${currentValue} / ${threshold.targetValue}`,
        });

        // Trigger specific Legendary 1000+ points modal notification
        if (threshold.tier === 'legendary' || threshold.targetValue >= 1000) {
          legendaryModalNotification = {
            isOpen: true,
            badgeId: threshold.id,
            badgeName: threshold.name,
            title: `LEGENDARY 1,000+ CAREER POINTS REACHED! 👑`,
            description: threshold.description,
            currentPoints: currentValue,
            targetPoints: threshold.targetValue,
            xpReward: threshold.xpReward,
            unlockedAt: new Date().toISOString().split('T')[0],
          };
        }
      }
    }
  });

  if (triggeredEliteConfetti) {
    fireEliteSeriesConfetti();
  }

  if (newlyUnlockedBadges.length === 0) {
    return { updatedUser: user, newlyUnlockedBadges: [] };
  }

  const updatedUser: AthleteProfile = {
    ...user,
    badges: currentBadges,
    proHighlights: currentProHighlights,
    xp: (user.xp || 0) + bonusXp,
  };

  return { updatedUser, newlyUnlockedBadges, legendaryModalNotification };
}

/**
 * Triggers Elite Series confetti burst and returns Legendary Notification Modal descriptor
 * when a user reaches or audits the 1000+ career points milestone.
 */
export function triggerLegendaryCareerThreshold(user: AthleteProfile): LegendaryNotificationInfo {
  fireEliteSeriesConfetti();

  const totalPts =
    (user.stats?.basketball?.pts || 0) +
    (user.stats?.soccer?.goals || 0) * 10 +
    (user.stats?.baseball?.hits || 0) * 5;

  return {
    isOpen: true,
    badgeId: 'badge_pro_top_1pct_scorer',
    badgeName: 'Top 1% League Scorer 👑',
    title: 'LEGENDARY 1,000+ CAREER POINTS MILESTONE! 👑',
    description: 'Congratulations! You have crossed the legendary 1,000+ career points threshold in official league and playground competition. Top 1% nationwide athlete rank achieved!',
    currentPoints: Math.max(1000, totalPts),
    targetPoints: 1000,
    xpReward: 2500,
    unlockedAt: new Date().toISOString().split('T')[0],
  };
}

